import React, { useMemo, useState } from 'react';
import { Confirm, Dropdown } from 'semantic-ui-react';
import { Table, HeaderCell, TableBody, TableCell, TableHeader, TableRow } from '../../../common/components/Table';
import { PageSection, Subtitle } from 'common/styles/page';
import { StyledIconButton } from 'common/components/button/StyledButton';
import { organizationRecordApi } from '../api/organizationRecordApi';
import { OrganizationRecordRead } from 'store/generated/admin/organization-records';
import OrganizationRecordDataModal from './OutcomesOrganizationRecordModal';
import LoadingSpinner from 'common/components/LoadingSpinner';
import { useSelector } from 'react-redux';
import { getAssessmentPeriods } from 'common/selectors/auth';
import { ASSESSMENT_PERIOD_CLOSED } from 'areas/assessment-schedule/constants';

interface OrganizationRecordsDataTableProps {
  organizationId: number;
  recordValueId: number;
  recordValueName: string;
}

const OrganizationRecordsDataTable: React.FC<OrganizationRecordsDataTableProps> = ({
  organizationId,
  recordValueId,
  recordValueName,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<OrganizationRecordRead | null>(null);
  const [selectedAssessmentPeriodId, setSelectedAssessmentPeriodId] = useState<number | undefined>(undefined);
  const [selectedAssessmentPeriodName, setSelectedAssessmentPeriodName] = useState<string>('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState<OrganizationRecordRead | null>(null);

  const assessmentPeriods = useSelector(getAssessmentPeriods);

  // Get all closed assessment periods
  const closedAssessmentPeriods = useMemo(() => {
    if (!assessmentPeriods) return [];
    return assessmentPeriods
      .filter((period: any) => period.status === ASSESSMENT_PERIOD_CLOSED)
      .sort((a: any, b: any) => {
        const aDate = new Date(a.startDateTime).getTime();
        const bDate = new Date(b.startDateTime).getTime();
        return bDate - aDate; // Most recent first
      });
  }, [assessmentPeriods]);

  const { data: records, isLoading, error } = organizationRecordApi.endpoints.apiAdminV1OrganizationRecordsList.useQuery({
    organizationId,
  });

  const [deleteRecord] = organizationRecordApi.endpoints.apiAdminV1OrganizationRecordsDestroy.useMutation();

  const confirmDelete = async () => {
    if (!recordToDelete) return;
    try {
      await deleteRecord({ id: recordToDelete.id }).unwrap();
      setShowDeleteModal(false);
      setRecordToDelete(null);
    } catch (err) {
      console.error('Failed to delete record:', err);
    }
  };

  const handleAddOrEdit = (record?: OrganizationRecordRead, periodId?: number, periodName?: string) => {
    setSelectedRecord(record || null);
    setSelectedAssessmentPeriodId(periodId);
    setSelectedAssessmentPeriodName(periodName || '');
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedRecord(null);
    setSelectedAssessmentPeriodId(undefined);
    setSelectedAssessmentPeriodName('');
  };

  // Filter records for the selected record value
  const filteredRecords = useMemo(() => {
    if (!records) return [];
    return records.filter((record: any) => {
      const recordValueIdFromApi = typeof record.organization_record_value === 'object' 
        ? record.organization_record_value?.id 
        : record.organization_record_value;
      return recordValueIdFromApi === recordValueId || recordValueIdFromApi === recordValueId.toString();
    });
  }, [records, recordValueId]);

  // Create a map of assessment period ID to record for quick lookup
  const recordsByPeriod = useMemo(() => {
    const map = new Map<number, any>();
    filteredRecords.forEach((record: any) => {
      if (record.assessment_period) {
        const periodId = typeof record.assessment_period === 'object'
          ? record.assessment_period.id
          : parseInt(record.assessment_period);
        map.set(periodId, record);
      }
    });
    return map;
  }, [filteredRecords]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div style={{ padding: '24px' }}>
        <h2 style={{ color: 'red' }}>Error loading organization records</h2>
      </div>
    );
  }

  return (
    <PageSection>
      <Table>
        <TableHeader>
          <TableRow>
            <HeaderCell width={8}>Assessment Period</HeaderCell>
            <HeaderCell width={6}>Value</HeaderCell>
            <HeaderCell width={2} justify="center">Actions</HeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {closedAssessmentPeriods.length === 0 ? (
            <TableRow>
              <TableCell colSpan={3} style={{ textAlign: 'center', fontStyle: 'italic', color: '#888' }}>
                No closed assessment periods available
              </TableCell>
            </TableRow>
          ) : (
            closedAssessmentPeriods.map((period: any) => {
              const existingRecord = recordsByPeriod.get(period.id);
              return (
                <TableRow
                  key={period.id}
                  $selectable
                  onClick={() => handleAddOrEdit(existingRecord, period.id, period.name)}
                >
                  <TableCell $firstcol>{period.name}</TableCell>
                  <TableCell>
                    {existingRecord ? existingRecord.value : '-'}
                  </TableCell>
                  <TableCell justify="center" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                    {existingRecord ? (
                      <Dropdown
                        button
                        icon="ellipsis vertical"
                        floating
                        className="icon"
                        style={{ width: '38px', height: '35px', borderRadius: '8px' }}
                        direction="left"
                      >
                        <Dropdown.Menu>
                          <Dropdown.Item
                            icon="edit"
                            text="Edit"
                            onClick={() => handleAddOrEdit(existingRecord, period.id, period.name)}
                          />
                          <Dropdown.Item
                            icon="trash"
                            text="Delete"
                            onClick={() => {
                              setRecordToDelete(existingRecord);
                              setShowDeleteModal(true);
                            }}
                          />
                        </Dropdown.Menu>
                      </Dropdown>
                    ) : (
                      <StyledIconButton
                        icon="plus"
                        title="Add Record"
                        onClick={() => handleAddOrEdit(undefined, period.id, period.name)}
                        color="green"
                      />
                    )}
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>

      <OrganizationRecordDataModal
        open={isModalOpen}
        onClose={handleCloseModal}
        organizationId={organizationId}
        recordValueId={recordValueId}
        recordValueName={recordValueName}
        assessmentPeriodId={selectedAssessmentPeriodId}
        assessmentPeriodName={selectedAssessmentPeriodName}
        existingRecord={selectedRecord}
      />

      <Confirm
        open={showDeleteModal}
        onCancel={() => {
          setShowDeleteModal(false);
          setRecordToDelete(null);
        }}
        onConfirm={confirmDelete}
        content={`Are you sure you want to delete this ${recordValueName.toLowerCase()} record?`}
      />
    </PageSection>
  );
};

export default OrganizationRecordsDataTable;
